import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts'
import { Typogaphy } from 'vnetwork-ui'

export type TicketStatusType =
    | 'opening'
    | 'work-in-progress'
    | 'closed'
    | 'completed'

export interface TicketStatusSummary {
    ticket_status: TicketStatusType
    total_tickets: number
}

interface TicketStatusPieChartProps {
    ticket_status_summary: TicketStatusSummary[]
}

const STATUS_LABELS: Record<TicketStatusType, string> = {
    opening: 'New',
    'work-in-progress': 'In Progress',
    completed: 'Resolved',
    closed: 'Closed',
}

const STATUS_ORDER: TicketStatusType[] = [
    'opening',
    'work-in-progress',
    'completed',
    'closed',
]

const STATUS_COLORS: Record<TicketStatusType, string> = {
    opening: '#3B82F6',
    'work-in-progress': '#F59E0B',
    completed: '#10B981',
    closed: '#6B7280',
}

const TicketStatusPieChart: React.FC<TicketStatusPieChartProps> = ({
    ticket_status_summary = [],
}) => {
    const { t } = useTranslation('ticket')

    // Sort by defined order: New -> In Progress -> Resolved -> Closed
    const chartData = useMemo(() => {
        return [...ticket_status_summary]
            .sort((a, b) => {
                const orderA = STATUS_ORDER.indexOf(a.ticket_status)
                const orderB = STATUS_ORDER.indexOf(b.ticket_status)
                return orderA - orderB
            })
            .map((item) => ({
                name: STATUS_LABELS[item.ticket_status] || item.ticket_status,
                value: item.total_tickets,
                color: STATUS_COLORS[item.ticket_status] || '#9CA3AF',
            }))
    }, [ticket_status_summary])

    const total = useMemo(() => {
        return ticket_status_summary.reduce(
            (acc, item) => acc + item.total_tickets,
            0
        )
    }, [ticket_status_summary])

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null

        const data = payload[0]
        return (
            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                <p className="font-semibold text-gray-800">{data.name}</p>
                <p className="text-sm" style={{ color: data.payload.color }}>
                    {data.value} tickets (
                    {((data.value / total) * 100).toFixed(1)}%)
                </p>
            </div>
        )
    }

    const renderCustomLabel = ({
        cx,
        cy,
        midAngle,
        outerRadius,
        value,
    }: any) => {
        if (value === 0) return null
        const RADIAN = Math.PI / 180
        const radius = outerRadius + 20
        const x = cx + radius * Math.cos(-midAngle * RADIAN)
        const y = cy + radius * Math.sin(-midAngle * RADIAN)

        return (
            <text
                x={x}
                y={y}
                fill="#374151"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                fontSize={11}
                fontWeight="bold"
            >
                {value}
            </text>
        )
    }

    return (
        <div className="bg-white rounded-[8px] p-4 lg:col-span-4 col-span-12 half-col-print">
            <Typogaphy
                size="small"
                className="text-primary font-semibold capitalize"
            >
                {t('ticket-status')} ({total})
            </Typogaphy>

            <div className="min-h-[300px] mt-10 flex items-center justify-center">
                {ticket_status_summary.length === 0 ? (
                    <Typogaphy className="text-primary text-center mt-10 text-sm">
                        {t('no-data')}
                    </Typogaphy>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="45%"
                                outerRadius={90}
                                dataKey="value"
                                label={renderCustomLabel}
                                labelLine={{
                                    stroke: '#9ca3af',
                                    strokeWidth: 1,
                                }}
                                stroke="#fff"
                                strokeWidth={2}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                                verticalAlign="bottom"
                                height={24}
                                content={({ payload }) => {
                                    // Sort by STATUS_ORDER: New -> In Progress -> Resolved -> Closed
                                    const labelOrder = STATUS_ORDER.map(
                                        (key) => STATUS_LABELS[key]
                                    )
                                    const sortedPayload = [
                                        ...(payload || []),
                                    ].sort((a, b) => {
                                        const orderA = labelOrder.indexOf(
                                            a.value as string
                                        )
                                        const orderB = labelOrder.indexOf(
                                            b.value as string
                                        )
                                        return orderA - orderB
                                    })
                                    return (
                                        <div className="flex justify-center gap-6">
                                            {sortedPayload.map(
                                                (entry, index) => (
                                                    <div
                                                        key={`legend-${index}`}
                                                        className="flex items-center gap-1.5"
                                                    >
                                                        <div
                                                            className="w-2.5 h-2.5 rounded-sm"
                                                            style={{
                                                                backgroundColor:
                                                                    entry.color,
                                                            }}
                                                        />
                                                        <span className="text-xs text-gray-600">
                                                            {entry.value}
                                                        </span>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    )
}

export default TicketStatusPieChart
